"use client"
import { useState, useEffect, useRef } from "react"
import { Button } from "../../components/ui/button"
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "../../components/ui/card"
import { Input } from "../../components/ui/input"
import { getTempToken } from "../../api/authService"
import { ImagePickerModal } from "./image-picker-modal"
import { VariableInsertionPanel } from "./variable-insertion-panel"
import ReactQuill from "react-quill"
import "react-quill/dist/quill.snow.css"


interface EmailTemplate {
  id?: number
  title: string
  description: string
  html: string
  variables: string[]
  image_ids: number[]
}

interface TemplateEditorProps {
  template?: EmailTemplate
  onSave: () => void
  onCancel: () => void
}

export function TemplateEditor({ template, onSave, onCancel }: TemplateEditorProps) {
  const [title, setTitle] = useState(template?.title || "")
  const [description, setDescription] = useState(template?.description || "")
  const [html, setHtml] = useState(template?.html || "")
  const [variables, setVariables] = useState<string[]>(template?.variables || [])
  const [imageIds, setImageIds] = useState<number[]>(template?.image_ids || [])
  const [saving, setSaving] = useState(false)
  const [showImagePicker, setShowImagePicker] = useState(false)
  const [selectedImagePos, setSelectedImagePos] = useState<number | null>(null)
  const quillRef = useRef<any>(null)

  const modules = {
    toolbar: [
      [{ header: [1, 2, 3, false] }],
      ["bold", "italic", "underline", "strike"],
      [{ color: [] }, { background: [] }],
      [{ list: "ordered" }, { list: "bullet" }],
      [{ align: [] }],
      ["link"],
      [{ script: "sub" }, { script: "super" }],
      ["clean"],
      ["insertImage", "insertVariable"],
    ],
  }

  const handleInsertImage = () => {
    setSelectedImagePos(quillRef.current?.getEditor().getSelection()?.index || 0)
    setShowImagePicker(true)
  }

  const handleInsertVariable = () => {
    // Placeholder - will be handled by VariableInsertionPanel
  }

  // Configure custom toolbar buttons
  useEffect(() => {
    if (quillRef.current) {
      const toolbar = quillRef.current.getEditor().getModule("toolbar")
      toolbar.addHandler("insertImage", handleInsertImage)
      toolbar.addHandler("insertVariable", handleInsertVariable)
    }
  }, [quillRef])

  const handleImageSelected = (imageUrl: string, imageId: number) => {
    if (selectedImagePos !== null && quillRef.current) {
      const editor = quillRef.current.getEditor()
      editor.insertEmbed(selectedImagePos, "image", imageUrl)

      // Add image ID to tracking
      if (!imageIds.includes(imageId)) {
        setImageIds([...imageIds, imageId])
      }
    }
    setShowImagePicker(false)
    setSelectedImagePos(null)
  }

  const handleAddVariable = (variableName: string) => {
    if (quillRef.current) {
      const editor = quillRef.current.getEditor()
      const selection = editor.getSelection()
      if (selection) {
        editor.insertText(selection.index, `[@${variableName}]`)
      }
    }

    if (!variables.includes(variableName)) {
      setVariables([...variables, variableName])
    }
  }

  const handleSave = async () => {
    if (!title || !html) {
      alert("Please fill in all required fields")
      return
    }

    setSaving(true)
    try {
      const token = await getTempToken()
      const method = template?.id ? "PUT" : "POST"
      const url = template?.id
        ? `https://tsoministries.com/wp-json/custom-api/v1/email-templates/${template.id}`
        : "https://tsoministries.com/wp-json/custom-api/v1/email-templates"

      const res = await fetch(url, {
        method,
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify({
          title,
          description,
          html,
          variables,
          image_ids: imageIds,
        }),
      })

      if (res.ok) {
        onSave()
      } else {
        const error = await res.json()
        alert(error.message || "Failed to save template")
      }
    } catch (error) {
      console.error("[v0] Error saving template:", error)
      alert("Error saving template")
    } finally {
      setSaving(false)
    }
  }

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-2xl font-bold tracking-tight">{template ? "Edit" : "Create"} Template</h2>
        </div>
        <div className="flex gap-2">
          <Button onClick={onCancel} variant="outline">
            Cancel
          </Button>
          <Button onClick={handleSave} disabled={saving}>
            {saving ? "Saving..." : template ? "Update" : "Create"} Template
          </Button>
        </div>
      </div>

      <Card>
        <CardHeader>
          <CardTitle>Template Details</CardTitle>
        </CardHeader>
        <CardContent className="space-y-4">
          <div>
            <label className="text-sm font-medium">Template Title *</label>
            <Input
              placeholder="e.g., Welcome Email, Newsletter Template"
              value={title}
              onChange={(e) => setTitle(e.target.value)}
              className="mt-1"
            />
          </div>
          <div>
            <label className="text-sm font-medium">Description</label>
            <Input
              placeholder="Optional description of this template"
              value={description}
              onChange={(e) => setDescription(e.target.value)}
              className="mt-1"
            />
          </div>
        </CardContent>
      </Card>

      <Card>
        <CardHeader>
          <CardTitle>Template Editor</CardTitle>
          <CardDescription>
            Use the toolbar to format your template. Add images and variables as needed.
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="border rounded-md bg-white">
            <ReactQuill
              ref={quillRef}
              theme="snow"
              value={html}
              onChange={setHtml}
              modules={modules}
              style={{ height: "400px", marginBottom: "40px" }}
            />
          </div>

          <div className="flex gap-2">
            <Button onClick={() => setShowImagePicker(true)} variant="outline" className="gap-2">
              Add Image
            </Button>
            <VariableInsertionPanel onVariableSelect={handleAddVariable} />
          </div>
        </CardContent>
      </Card>

      <Card>
        <CardHeader>
          <CardTitle>Variables Used</CardTitle>
          <CardDescription>Variables that will be substituted when sending emails</CardDescription>
        </CardHeader>
        <CardContent>
          {variables.length === 0 ? (
            <p className="text-sm text-gray-500">No variables added yet</p>
          ) : (
            <div className="flex flex-wrap gap-2">
              {variables.map((v) => (
                <span
                  key={v}
                  className="px-3 py-1 bg-blue-100 text-blue-700 rounded-full text-sm font-medium flex items-center gap-2"
                >
                  [@{v}]
                  <button
                    onClick={() => setVariables(variables.filter((x) => x !== v))}
                    className="hover:text-blue-900 ml-1"
                  >
                    ×
                  </button>
                </span>
              ))}
            </div>
          )}
        </CardContent>
      </Card>

      {showImagePicker && (
        <ImagePickerModal onImageSelected={handleImageSelected} onClose={() => setShowImagePicker(false)} />
      )}
    </div>
  )
}
